import {} from 'vue';
import { useResizeObserver, useIntersectionObserver } from '@vueuse/core';
/**
 * Force Firefox to composite a freshly-created Chart.js chart.
 *
 * Chart.js draws into the canvas synchronously at creation (animation is disabled), but
 * Firefox does not always composite that canvas to screen until a later repaint is forced —
 * a mouse hover (which triggers a Chart.js re-render) or a window resize. The result is a
 * blank/partial chart that only appears on interaction. Chrome composites eagerly, so it
 * never shows the bug.
 *
 * Calling `chart.render()` on the next animation frame performs that repaint in a normal
 * frame, so the already-drawn content is composited. Note `resize()` is NOT used: with an
 * unchanged container size it early-returns without redrawing, so it would not fix this.
 */
export function repaintChartNextFrame(getChart) {
    requestAnimationFrame(() => getChart()?.render?.());
}
/**
 * Re-render a chart whenever its element scrolls into view.
 *
 * Firefox does not composite a canvas that was drawn while off-screen when it is later scrolled
 * into view, so charts created below the fold stay blank. Observing the element and calling
 * `render()` on intersection composites it. `render()` only redraws — it never resizes — so it is
 * safe alongside Chart.js's own `responsive` observer and does not disturb zoom/pan state.
 *
 * @param target    Element (or getter/ref) to observe — the container or the canvas itself.
 * @param getChart  Returns the current chart instance (or a falsy value if none).
 */
export function useChartIntersectionRepaint(target, getChart) {
    useIntersectionObserver(target, (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
            getChart()?.render?.();
        }
    });
}
/**
 * Create a Chart.js chart once its container has a real (non-zero) size, then keep it painted.
 *
 * Two Firefox-specific problems are handled:
 *
 * 1. **Size at construction** — Firefox does not always resolve a container's aspect-ratio-derived
 *    height in the synchronous layout pass Chart.js forces at `new Chart()` time. A chart built
 *    eagerly in `onMounted` then measures ~0 and draws nothing. The `ResizeObserver` gate defers
 *    `create()` until the container reports a non-zero size, guaranteeing a correct measurement.
 *
 * 2. **Compositing** — even once drawn, Firefox does not reliably composite the canvas: an
 *    in-view chart stays blank until an interaction/repaint, and a chart drawn while **off-screen**
 *    is not painted when later scrolled into view. `repaintChartNextFrame` handles the in-view
 *    case; the `IntersectionObserver` re-renders whenever the chart scrolls into view, which
 *    composites off-screen-created charts.
 *
 * It never calls `chart.resize()`: Chart.js's own `responsive: true` ResizeObserver remains the
 * single owner of all resizing, so there is no double-observer conflict. `render()` only redraws.
 *
 * @param container  Template ref of the element whose size Chart.js measures
 *                   (the `absolute inset-0` box wrapping the canvas).
 * @param create     Creates the chart. Called only when the container is non-zero and no
 *                   chart exists yet; may itself no-op if its data isn't ready.
 * @param getChart   Returns the current chart instance (or a falsy value if none).
 */
export function useDeferredChart(container, { create, getChart }) {
    // Gate creation on a real size (issue #1) + repaint for the in-view case (issue #2).
    useResizeObserver(container, (entries) => {
        const rect = entries[0]?.contentRect;
        if (rect && rect.width > 0 && rect.height > 0 && !getChart()) {
            create();
            repaintChartNextFrame(getChart);
        }
    });
    // Re-render whenever the chart scrolls into view so Firefox composites a chart that was
    // created while off-screen (issue #2). render() is cheap and does not resize.
    useChartIntersectionRepaint(container, getChart);
}
